/**
 * Delete Pack Confirmation Modal
 * 
 * Displays a warning before permanently deleting a pack.
 * Shows what will be deleted and requires explicit confirmation.
 */

import React, { useState } from 'react';

interface DeletePackModalProps {
  packId: string;
  address?: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

export default function DeletePackModal({
  packId: _packId,
  address,
  isOpen,
  onClose,
  onConfirm,
}: DeletePackModalProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Close on Escape key
  React.useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isDeleting) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isDeleting, onClose]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setError(null);
    setIsDeleting(true);

    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete pack');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white w-full max-w-md shadow-lg"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-pack-title"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 id="delete-pack-title" className="text-lg font-semibold text-slate-950">
              Delete pack?
            </h2>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {/* Warning */}
            <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-600">
              <p className="text-sm font-medium text-red-800">
                This action is irreversible
              </p>
            </div>

            {/* Pack Info */}
            {address && (
              <p className="mb-4 text-sm text-slate-600">
                Pack: <strong>{address}</strong>
              </p>
            )}

            {/* What will be deleted */}
            <div className="mb-4">
              <p className="text-sm font-medium text-slate-900 mb-2">
                The following will be permanently deleted:
              </p>
              <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                <li>All uploaded documents</li>
                <li>All disclosure answers</li>
                <li>Generated forms and PDFs</li>
                <li>Pack record and metadata</li>
              </ul>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-sm text-red-600" role="alert">
                {error}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isDeleting}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isDeleting}
              className="btn-danger"
            >
              {isDeleting ? 'Deleting...' : 'Delete pack'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
